// 3.0 doesn't sell a second venue self-serve: an owner at their plan's limit
// sees "Request another venue" instead of a blocked "Add venue", and that
// request lands here. Work it by opening the org, emailing an invoice or
// payment link (each additional venue is its own $49.99/mo or $449/yr
// location), and raising the org's venue limit there — only then mark this
// fulfilled, since that's what actually lets the owner add the venue.
// Decline otherwise.

import { el, clear, fmtDate } from '../ui/dom';
import { renderShell, renderError, renderLoading } from '../ui/shell';
import { UnauthorizedError } from '../api/client';
import { navigate } from '../router';
import type { RouteContext } from '../router';
import {
  listVenueRequests,
  setVenueRequestStatus,
  type AdminVenueRequestRow,
  type VenueRequestStatus,
} from '../api/venue-requests';

const STATUS_OPTIONS: [VenueRequestStatus, string][] = [
  ['open', 'Open'],
  ['fulfilled', 'Fulfilled'],
  ['declined', 'Declined'],
];

export async function renderVenueRequests({ root, query }: RouteContext): Promise<void> {
  const content = renderShell(root, 'venue-requests');

  const state = {
    status: (query.get('status') as VenueRequestStatus | null) ?? 'open',
  };
  if (!STATUS_OPTIONS.some(([v]) => v === state.status)) state.status = 'open';

  const statusSelect = el('select', { class: 'control' });
  for (const [value, label] of STATUS_OPTIONS) {
    const opt = el('option', { value, textContent: label });
    if (value === state.status) opt.selected = true;
    statusSelect.append(opt);
  }

  const results = el('div', { class: 'results' });
  content.append(
    el('div', { class: 'page-head' }, [el('h1', {}, ['Venue requests'])]),
    el('p', { class: 'view-caption' }, [
      'Owners at their plan’s venue limit who asked to add another location. Open the org, email an invoice or payment link, and raise the venue limit there — then mark the request fulfilled. Decline it if it won’t go ahead.',
    ]),
    el('div', { class: 'controls' }, [
      el('div', {}, []),
      el('div', { class: 'controls-right' }, [statusSelect]),
    ]),
    results,
  );

  function syncUrl(): void {
    history.replaceState(
      {},
      '',
      state.status === 'open' ? '/venue-requests' : `/venue-requests?status=${state.status}`,
    );
  }

  async function load(): Promise<void> {
    renderLoading(results, 'Loading venue requests…');
    syncUrl();
    try {
      const { requests } = await listVenueRequests(state.status);
      renderTable(results, requests, resolveRequest);
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(
        results,
        err instanceof Error ? err.message : 'Failed to load venue requests.',
      );
    }
  }

  statusSelect.addEventListener('change', () => {
    state.status = statusSelect.value as VenueRequestStatus;
    void load();
  });

  /**
   * Confirmed — marking a request resolved is a support commitment, not a
   * label that undoes itself. "Fulfilled" specifically means the org's
   * venue limit has already been raised (Open org → Venue limit), not just
   * that an invoice went out — owner clients key off request status, so
   * marking it fulfilled early re-offers "Add venue" while the org is still
   * at its limit. "Declined" means the request won't go ahead.
   */
  async function resolveRequest(
    row: AdminVenueRequestRow,
    status: 'fulfilled' | 'declined',
  ): Promise<void> {
    const verb = status === 'fulfilled' ? 'fulfilled' : 'declined';
    const question =
      status === 'fulfilled'
        ? `Mark ${row.organizationName}’s request for “${row.locationName}” as fulfilled? Only do this after you’ve raised the org’s venue limit (Open org → Venue limit) — marking it fulfilled first leaves the owner still blocked.`
        : `Mark ${row.organizationName}’s request for “${row.locationName}” as declined?`;
    if (!window.confirm(question)) {
      return;
    }
    try {
      await setVenueRequestStatus(row.id, status);
      void load();
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(
        results,
        err instanceof Error ? err.message : `Failed to mark that request ${verb}.`,
      );
    }
  }

  await load();
}

function renderTable(
  container: HTMLElement,
  rows: AdminVenueRequestRow[],
  onResolve: (row: AdminVenueRequestRow, status: 'fulfilled' | 'declined') => void,
): void {
  clear(container);
  if (rows.length === 0) {
    container.append(el('div', { class: 'empty' }, ['No requests match.']));
    return;
  }

  const headers = ['Org', 'Owner', 'Requested location', 'Note', 'Requested', 'Status', ''];
  const thead = el('thead', {}, [
    el('tr', {}, headers.map((h) => el('th', { textContent: h }))),
  ]);

  const body = rows.map((r) => {
    const cityRegion = [r.city, r.region].filter(Boolean).join(', ');

    const actionButtons: HTMLElement[] = [
      el(
        'button',
        {
          class: 'btn btn-ghost btn-sm',
          type: 'button',
          onClick: () => navigate(`/organizations/${r.organizationId}`),
        },
        ['Open org'],
      ),
    ];
    if (r.status === 'open') {
      actionButtons.push(
        el(
          'button',
          {
            class: 'btn btn-ghost btn-sm',
            type: 'button',
            onClick: () => onResolve(r, 'fulfilled'),
          },
          ['Mark fulfilled'],
        ),
        el(
          'button',
          {
            class: 'btn btn-ghost btn-sm btn-danger',
            type: 'button',
            onClick: () => onResolve(r, 'declined'),
          },
          ['Decline'],
        ),
      );
    }

    return el('tr', {}, [
      el('td', { class: 'cell-strong', textContent: r.organizationName }),
      el('td', { class: 'cell-muted', textContent: r.ownerEmail ?? '—' }),
      el(
        'td',
        { style: 'white-space:normal;min-width:160px;max-width:280px;overflow-wrap:anywhere' },
        [
          el('div', { class: 'cell-strong', textContent: r.locationName }),
          cityRegion ? el('div', { class: 'cell-muted', textContent: cityRegion }) : null,
        ],
      ),
      el('td', {
        class: 'cell-muted',
        style: 'white-space:normal;min-width:220px;max-width:420px;overflow-wrap:anywhere',
        textContent: r.note || '—',
      }),
      el('td', { class: 'cell-muted', textContent: fmtDate(r.createdAt) }),
      el('td', {}, [
        el('span', {
          class: statusPillClass(r.status),
          textContent: statusLabel(r.status),
        }),
      ]),
      el('td', {}, [
        el('div', { class: 'button-row', style: 'justify-content:flex-start' }, actionButtons),
      ]),
    ]);
  });

  container.append(
    el('div', { class: 'table-wrap' }, [
      el('table', { class: 'data-table' }, [thead, el('tbody', {}, body)]),
    ]),
  );
}

function statusPillClass(status: VenueRequestStatus): string {
  switch (status) {
    case 'fulfilled':
      return 'pill pill-ok';
    case 'declined':
      return 'pill pill-danger';
    default:
      return 'pill pill-warn';
  }
}

function statusLabel(status: VenueRequestStatus): string {
  switch (status) {
    case 'fulfilled':
      return 'Fulfilled';
    case 'declined':
      return 'Declined';
    default:
      return 'Open';
  }
}
